import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope"
import type {
  ConsentResult,
  Disclosure,
  OpenedLink,
  SigningRefusal,
  VerifyResult
} from "../types/signing.types"
import { signingClient, withSession } from "./signing.client"

const ROOT = "/esign/public"

// Long enough for a browser that reads the blob after the click returns.
const REVOKE_AFTER_MS = 60_000

export interface SigningResponse<T> {
  ok: boolean
  status: number
  data: T | null
  refusal: SigningRefusal | null
}

const asResponse = <T>(status: number, body: unknown): SigningResponse<T> => {
  if (status >= 200 && status < 300) {
    // A write comes back wrapped, and reading it straight yields undefined
    // fields with nothing raising.
    return { ok: true, status, data: unwrapEnvelope<T>(body), refusal: null }
  }
  return {
    ok: false,
    status,
    data: null,
    refusal: (body ?? { reason: "unknown" }) as SigningRefusal
  }
}

/**
 * Hand the token over once, in a body rather than a URL.
 *
 * It arrives in the page's fragment, which no browser sends to a server. Putting
 * it back into a path or a query string would undo that in one step: the
 * backend's access log, the CDN's log and any error report would all have it.
 */
export const openSigningLink = async (token: string) => {
  const { status, data } = await signingClient.post(`${ROOT}/open/`, { token })
  return asResponse<OpenedLink>(status, data)
}

export const requestCode = async (session: string) => {
  const { status, data } = await signingClient.post(
    `${ROOT}/code/send/`,
    {},
    withSession(session)
  )
  return asResponse<{ sent_to: string }>(status, data)
}

export const verifyCode = async (session: string, code: string) => {
  const { status, data } = await signingClient.post(
    `${ROOT}/code/verify/`,
    { code },
    withSession(session)
  )
  return asResponse<VerifyResult>(status, data)
}

export const fetchDisclosure = async (session: string) => {
  const { status, data } = await signingClient.get(
    `${ROOT}/disclosure/`,
    withSession(session)
  )
  return asResponse<Disclosure>(status, data)
}

export const giveConsent = async (session: string, textSha256: string) => {
  const { status, data } = await signingClient.post(
    `${ROOT}/consent/`,
    { agreed: true, text_sha256: textSha256 },
    withSession(session)
  )
  return asResponse<ConsentResult>(status, data)
}

export const withdrawConsent = async (session: string) => {
  const { status, data } = await signingClient.post(
    `${ROOT}/consent/withdraw/`,
    {},
    withSession(session)
  )
  return asResponse<{ withdrawn: boolean }>(status, data)
}

export const requestPaperCopy = async (session: string) => {
  const { status, data } = await signingClient.post(
    `${ROOT}/paper-copy/`,
    {},
    withSession(session)
  )
  return asResponse<{ requested: boolean }>(status, data)
}

/**
 * Fetch the document as bytes and hand back a blob URL.
 *
 * The file is streamed through the API rather than served from a link of its
 * own, so it cannot be read without the session — and the viewer needs a URL,
 * which is what a blob gives us. The same pattern the app already uses for PDF
 * previews.
 */
export const fetchDocument = async (
  session: string,
  { download = false }: { download?: boolean } = {}
): Promise<SigningResponse<string>> => {
  const response = await signingClient.get(`${ROOT}/document/`, {
    ...withSession(session),
    params: download ? { download: "true" } : undefined,
    responseType: "blob"
  })
  const blob = response.data as Blob
  if (response.status >= 200 && response.status < 300) {
    return {
      ok: true,
      status: response.status,
      data: URL.createObjectURL(blob),
      refusal: null
    }
  }
  // A refusal on this route arrives as JSON, but the request asked for a blob,
  // so the reason has to be read back out of it before it can be shown.
  return {
    ok: false,
    status: response.status,
    data: null,
    refusal: await refusalInBlob(blob)
  }
}

const refusalInBlob = async (blob: Blob): Promise<SigningRefusal> => {
  try {
    return JSON.parse(await blob.text()) as SigningRefusal
  } catch {
    return { reason: "unknown" }
  }
}

/**
 * Save the document, as its own recorded event.
 *
 * The blob already on the page would download without the server ever hearing
 * about it, and a download is the one open the audit trail most wants to name.
 */
export const downloadDocument = async (
  session: string,
  filename: string
): Promise<SigningResponse<string>> => {
  const response = await fetchDocument(session, { download: true })
  if (!response.ok || !response.data) return response

  const url = response.data
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Firefox and Safari read the blob after the click returns, so revoking here
  // cancels the save. This is how a signer keeps their copy of a contract.
  window.setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS)
  return response
}
